/**
 * React Query mutation hooks for benefit plan group data modifications
 *
 * These hooks handle creates, updates, deletes, and reordering for benefit plan groups
 * with direct cache updates to avoid unnecessary refetches.
 */

import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { CoverageTypeEnum, PlanOptionNameEnum } from "@platform/openapi-types";
import { useApiErrorHandler } from "@platform/hooks";

import {
  createBenefitPlanGroup,
  deleteBenefitPlan,
  deleteBenefitPlanGroup,
  duplicateBenefitPlan,
  reorderBenefitPlanGroups,
  reorderBenefitPlansInGroup,
  updateBenefitPlanGroup,
  updateBenefitPlanOptionName,
} from "./clientBenefitPlanGroups";
import { queryKeys } from "./queries";
import { useApiClient } from "./useApiClient";

import type {
  BenefitPlanGroup,
  BenefitPlanSummary,
  CreateBenefitPlanGroupRequest,
  DuplicateBenefitPlanGroupRequest,
  ReorderBenefitPlanGroupsRequest,
  ReorderBenefitPlanGroupsResponse,
  ReorderBenefitPlansInGroupRequest,
  ReorderBenefitPlansInGroupResponse,
  RfpData,
  UpdateBenefitPlanGroupRequest,
  UpdateBenefitPlanGroupResponse,
} from "./types";

/** Query key for the coverage summary that backs the LOC completion badge. */
const COVERAGE_SUMMARY_QUERY_KEY = "coverage-summary";

/**
 * Hook to create a benefit plan group
 *
 * Automatically invalidates benefit plan group queries on success.
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 */
export const useCreateBenefitPlanGroup = (
  options?: UseMutationOptions<
    BenefitPlanGroup,
    Error,
    CreateBenefitPlanGroupRequest
  >,
): UseMutationResult<
  BenefitPlanGroup,
  Error,
  CreateBenefitPlanGroupRequest
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Create Benefit Plan Group API is not mocked");
  }

  const { onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: (request: CreateBenefitPlanGroupRequest) =>
      createBenefitPlanGroup(request, axiosInstance),
    ...restOptions,
    onSuccess: (data, variables, ...otherArgs) => {
      queryClient.invalidateQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });

      // Invalidate RFP query to refresh coverage data with new plan
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfp(variables.rfpId),
      });

      // A new plan group starts incomplete, which flips the coverage's completion
      // server-side, so refresh the coverage summary that backs the completion badge.
      queryClient.invalidateQueries({
        queryKey: [COVERAGE_SUMMARY_QUERY_KEY],
      });

      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};

/**
 * Hook to duplicate a benefit plan group
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 */
export const useDuplicateBenefitPlanGroup = (
  options?: UseMutationOptions<
    BenefitPlanGroup,
    Error,
    DuplicateBenefitPlanGroupRequest & {
      rfpId: string;
      coverageType: CoverageTypeEnum;
    }
  >,
): UseMutationResult<
  BenefitPlanGroup,
  Error,
  DuplicateBenefitPlanGroupRequest & {
    rfpId: string;
    coverageType: CoverageTypeEnum;
  }
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Duplicate Benefit Plan Group API is not mocked");
  }

  const { onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: ({
      rfpId: _rfpId,
      coverageType: _coverageType,
      ...request
    }: DuplicateBenefitPlanGroupRequest & {
      rfpId: string;
      coverageType: CoverageTypeEnum;
    }) => createBenefitPlanGroup(request, axiosInstance),
    ...restOptions,
    onSuccess: (data, variables, ...otherArgs) => {
      queryClient.invalidateQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });

      // Invalidate RFP query to refresh coverage data with duplicated plan
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfp(variables.rfpId),
      });

      // A duplicated plan group changes the coverage's completion server-side, so
      // refresh the coverage summary that backs the completion badge.
      queryClient.invalidateQueries({
        queryKey: [COVERAGE_SUMMARY_QUERY_KEY],
      });

      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};

/**
 * Hook to delete a benefit plan group
 *
 * Automatically invalidates benefit plan group queries on success.
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 */
export const useDeleteBenefitPlanGroup = (
  options?: UseMutationOptions<
    void,
    Error,
    { id: string; rfpId: string; coverageType: CoverageTypeEnum }
  >,
): UseMutationResult<
  void,
  Error,
  { id: string; rfpId: string; coverageType: CoverageTypeEnum }
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();
  const { onSuccess, onError, ...restOptions } = options || {};
  const handleError = useApiErrorHandler({ onError });

  if (!axiosInstance) {
    throw new Error("Delete Benefit Plan Group API is not mocked");
  }

  return useMutation({
    ...restOptions,
    mutationFn: ({
      id,
    }: {
      id: string;
      rfpId: string;
      coverageType: CoverageTypeEnum;
    }) => deleteBenefitPlanGroup(id, axiosInstance),
    onMutate: async (variables) => {
      // Cancel in-flight BPG queries so they don't overwrite the optimistic update
      await queryClient.cancelQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });

      // Snapshot previous cache for rollback on error
      const previousData = queryClient.getQueriesData<BenefitPlanGroup[]>({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });

      // Optimistically remove the group from the BPG cache immediately
      // so column definitions update in the same render cycle as row data,
      // preventing a flash of empty cells while the API round-trips.
      // Use partial key match (rfpId only) since the full key includes
      // productType which isn't available in the delete mutation variables.
      queryClient.setQueriesData<BenefitPlanGroup[]>(
        { queryKey: ["benefitPlanGroups", variables.rfpId] },
        (oldData) => oldData?.filter((group) => group.id !== variables.id),
      );

      return { previousData };
    },
    onError: (error, variables, context, ...rest) => {
      // Rollback: restore previous BPG cache on failure
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      handleError(error, variables, context, ...rest);
    },
    onSuccess: (data, variables, ...rest) => {
      onSuccess?.(data, variables, ...rest);
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch to ensure cache is in sync with server
      queryClient.invalidateQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfp(variables.rfpId),
      });
      // Removing a plan group can flip the coverage back to complete, so refresh the
      // coverage summary that backs the completion badge.
      queryClient.invalidateQueries({
        queryKey: [COVERAGE_SUMMARY_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.benefitClasses(variables.rfpId),
      });
    },
  });
};

/**
 * Hook to delete a single benefit plan (plan-level delete).
 *
 * Automatically invalidates benefit plan group and RFP queries on settle.
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 */
export const useDeleteBenefitPlan = (
  options?: UseMutationOptions<
    void,
    Error,
    { rfpId: string; benefitPlanId: string }
  >,
): UseMutationResult<void, Error, { rfpId: string; benefitPlanId: string }> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();
  const { onSuccess, onError, ...restOptions } = options || {};
  const handleError = useApiErrorHandler({ onError });

  if (!axiosInstance) {
    throw new Error("Delete Benefit Plan API is not mocked");
  }

  return useMutation({
    ...restOptions,
    mutationFn: ({ rfpId, benefitPlanId }) =>
      deleteBenefitPlan(rfpId, benefitPlanId, axiosInstance),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });

      const previousData = queryClient.getQueriesData<BenefitPlanGroup[]>({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });

      queryClient.setQueriesData<BenefitPlanGroup[]>(
        { queryKey: ["benefitPlanGroups", variables.rfpId] },
        (oldData) =>
          oldData?.map((group) => {
            const nextBenefitPlans = group.benefitPlans.filter(
              (plan) => plan.id !== variables.benefitPlanId,
            );

            if (nextBenefitPlans.length === group.benefitPlans.length) {
              return group;
            }

            return {
              ...group,
              benefitPlans: nextBenefitPlans,
              size: nextBenefitPlans.length,
            };
          }),
      );

      return { previousData };
    },
    onError: (error, variables, context, ...rest) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      handleError(error, variables, context, ...rest);
    },
    onSuccess: (data, variables, ...rest) => {
      onSuccess?.(data, variables, ...rest);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });
      queryClient.invalidateQueries({
        queryKey: ["coverage"],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfp(variables.rfpId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.benefitClasses(variables.rfpId),
      });
    },
  });
};

/**
 * Hook to update a benefit plan group (nickname and/or size)
 *
 * Automatically invalidates related queries on success.
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function PlanGroupEditor({ groupId }: { groupId: string }) {
 *   const mutation = useUpdateBenefitPlanGroup({
 *     onSuccess: (response) => {
 *       toast.success(`Updated to: ${response.group?.nickname}`);
 *     },
 *     onError: (error) => {
 *       toast.error(`Failed: ${error.message}`);
 *     }
 *   });
 *
 *   const handleNicknameChange = (nickname: string) => {
 *     mutation.mutate({
 *       id: groupId,
 *       nickname,
 *     });
 *   };
 *
 *   return (
 *     <input
 *       onChange={(e) => handleNicknameChange(e.target.value)}
 *       disabled={mutation.isPending}
 *     />
 *   );
 * }
 * ```
 */
export const useUpdateBenefitPlanGroup = (
  options?: UseMutationOptions<
    UpdateBenefitPlanGroupResponse,
    Error,
    UpdateBenefitPlanGroupRequest
  >,
): UseMutationResult<
  UpdateBenefitPlanGroupResponse,
  Error,
  UpdateBenefitPlanGroupRequest
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Update Benefit Plan Group API is not mocked");
  }

  const { onSuccess, onError, ...restOptions } = options || {};
  const handleError = useApiErrorHandler({ onError });

  return useMutation({
    mutationFn: (request: UpdateBenefitPlanGroupRequest) =>
      updateBenefitPlanGroup(request, axiosInstance),
    ...restOptions,
    onMutate: async (variables) => {
      // Only apply optimistic update when adding a plan (size increase)
      if (variables.size == null) return {};

      await queryClient.cancelQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });

      const previousData = queryClient.getQueriesData<BenefitPlanGroup[]>({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });

      queryClient.setQueriesData<BenefitPlanGroup[]>(
        { queryKey: ["benefitPlanGroups", variables.rfpId] },
        (oldData) =>
          oldData?.map((group) => {
            if (group.id !== variables.id) return group;

            const placeholder: BenefitPlanSummary = {
              id: crypto.randomUUID(),
              identifier: "",
              optionName: PlanOptionNameEnum.notApplicable,
            };

            return {
              ...group,
              size: variables.size!,
              benefitPlans: [...group.benefitPlans, placeholder],
            };
          }),
      );

      return { previousData };
    },
    onError: (error, variables, context, ...rest) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      handleError(error, variables, context, ...rest);
    },
    onSuccess: (data, variables, ...otherArgs) => {
      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.coverage({
          rfpId: variables.rfpId,
          coverageType: variables.coverageType,
        }),
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.rfp(variables.rfpId),
      });

      // Setting a Current Policy can flip the coverage's completion server-side
      // (a duplicate policy resets it to incomplete — ADR 10.22), so refresh the
      // coverage summary that backs the completion badge.
      queryClient.invalidateQueries({
        queryKey: [COVERAGE_SUMMARY_QUERY_KEY],
      });
    },
  });
};

/**
 * Hook to reorder benefit plan groups
 *
 * Automatically invalidates related queries on success.
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function PlanGroupReorder({ rfpId, coverageType }: Props) {
 *   const mutation = useReorderBenefitPlanGroups({
 *     onSuccess: () => {
 *       toast.success("Plan groups reordered successfully!");
 *     },
 *     onError: (error) => {
 *       toast.error(`Failed to reorder: ${error.message}`);
 *     }
 *   });
 *
 *   const handleReorder = (newOrder: string[]) => {
 *     mutation.mutate({
 *       rfpId,
 *       coverageType,
 *       productType: "ppo",
 *       benefitPlanGroups: newOrder.map(id => ({ id }))
 *     });
 *   };
 *
 *   return (
 *     <button
 *       onClick={() => handleReorder(["group-2", "group-1"])}
 *       disabled={mutation.isPending}
 *     >
 *       {mutation.isPending ? "Reordering..." : "Reorder"}
 *     </button>
 *   );
 * }
 * ```
 */
export const useReorderBenefitPlanGroups = (
  options?: UseMutationOptions<
    ReorderBenefitPlanGroupsResponse,
    Error,
    ReorderBenefitPlanGroupsRequest
  >,
): UseMutationResult<
  ReorderBenefitPlanGroupsResponse,
  Error,
  ReorderBenefitPlanGroupsRequest
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Reorder Benefit Plan Groups API is not mocked");
  }

  const { onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: (request: ReorderBenefitPlanGroupsRequest) =>
      reorderBenefitPlanGroups(request, axiosInstance),
    ...restOptions,
    onSuccess: (data, variables, ...otherArgs) => {
      queryClient.invalidateQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });

      // Invalidate coverage queries that might reference these groups
      queryClient.invalidateQueries({
        queryKey: ["coverage"],
      });

      // Invalidate RFP queries that might contain these groups
      queryClient.invalidateQueries({
        queryKey: ["rfp"],
      });
      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};

/**
 * Reorders a group's plans to match `orderedIds` and re-assigns the positional
 * identifiers (slot 1 keeps `…-1`, slot 2 keeps `…-2`, …) so the optimistic cache
 * mirrors what the backend regenerates on read. Returns the reordered plans plus a
 * `planId → new identifier` map so the RFP cache (which keys row data by identifier)
 * can be kept in sync. If the request doesn't cleanly cover the group, the original
 * plans are returned unchanged and the map is empty.
 */
const reorderGroupPlansOptimistically = (
  plans: BenefitPlanSummary[],
  orderedIds: string[],
): {
  plans: BenefitPlanSummary[];
  identifierByPlanId: Map<string, string>;
} => {
  const planById = new Map(plans.map((plan) => [plan.id, plan]));
  const identifiersInSlotOrder = plans.map((plan) => plan.identifier);
  const identifierByPlanId = new Map<string, string>();

  const reordered = orderedIds.map((id, slot) => {
    const plan = planById.get(id);
    if (!plan) return null;

    const identifier = identifiersInSlotOrder[slot] ?? plan.identifier;
    identifierByPlanId.set(id, identifier);

    return { ...plan, identifier };
  });

  const coversGroup =
    reordered.length === plans.length &&
    reordered.every((plan): plan is BenefitPlanSummary => plan !== null);

  if (!coversGroup) {
    return { plans, identifierByPlanId: new Map() };
  }

  return { plans: reordered as BenefitPlanSummary[], identifierByPlanId };
};

interface ReorderPlansContext {
  previousGroups: [readonly unknown[], BenefitPlanGroup[] | undefined][];
  previousRfp: RfpData | undefined;
}

/**
 * Hook to reorder the plans within a single benefit plan group.
 *
 * Optimistically reorders the cached plans and re-sequences their positional
 * identifiers so the grid settles into the final order on drop — without a
 * snap-back to the old order while the server round-trips. On error the caches are
 * rolled back; on settle they are invalidated to reconcile with the server.
 *
 * @param options - React Query mutation options
 * @returns Mutation result
 */
export const useReorderBenefitPlansInGroup = (
  options?: UseMutationOptions<
    ReorderBenefitPlansInGroupResponse,
    Error,
    ReorderBenefitPlansInGroupRequest & { rfpId: string },
    ReorderPlansContext
  >,
): UseMutationResult<
  ReorderBenefitPlansInGroupResponse,
  Error,
  ReorderBenefitPlansInGroupRequest & { rfpId: string },
  ReorderPlansContext
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Reorder Benefit Plans In Group API is not mocked");
  }

  const { onError, onSuccess, ...restOptions } = options || {};

  return useMutation({
    ...restOptions,
    mutationFn: ({ rfpId: _rfpId, ...request }) =>
      reorderBenefitPlansInGroup(request, axiosInstance),
    onMutate: async ({ rfpId, groupId, orderedIds }) => {
      // Cancel in-flight reads so a late response can't clobber the optimistic order.
      await queryClient.cancelQueries({
        queryKey: ["benefitPlanGroups", rfpId],
      });
      await queryClient.cancelQueries({ queryKey: queryKeys.rfp(rfpId) });

      const previousGroups = queryClient.getQueriesData<BenefitPlanGroup[]>({
        queryKey: ["benefitPlanGroups", rfpId],
      });
      const previousRfp = queryClient.getQueryData<RfpData>(
        queryKeys.rfp(rfpId),
      );

      let identifierByPlanId = new Map<string, string>();

      // Reorder the group's plans (drives column order) and capture the new
      // positional identifiers.
      queryClient.setQueriesData<BenefitPlanGroup[]>(
        { queryKey: ["benefitPlanGroups", rfpId] },
        (old) =>
          old?.map((group) => {
            if (group.id !== groupId) return group;

            const result = reorderGroupPlansOptimistically(
              group.benefitPlans,
              orderedIds,
            );
            if (result.identifierByPlanId.size > 0) {
              identifierByPlanId = result.identifierByPlanId;
            }

            return { ...group, benefitPlans: result.plans };
          }),
      );

      // Row data is keyed by identifier, so re-label the matching RFP plans too.
      if (identifierByPlanId.size > 0) {
        queryClient.setQueryData<RfpData>(queryKeys.rfp(rfpId), (old) =>
          old
            ? {
                ...old,
                coverages: old.coverages.map((coverage) => ({
                  ...coverage,
                  benefitPlans: coverage.benefitPlans.map((plan) =>
                    plan.id && identifierByPlanId.has(plan.id)
                      ? {
                          ...plan,
                          identifier: identifierByPlanId.get(plan.id)!,
                        }
                      : plan,
                  ),
                })),
              }
            : old,
        );
      }

      return { previousGroups, previousRfp };
    },
    onError: (error, variables, onMutateResult, context) => {
      // Roll back to the pre-drag order.
      onMutateResult?.previousGroups.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      if (onMutateResult && onMutateResult.previousRfp !== undefined) {
        queryClient.setQueryData(
          queryKeys.rfp(variables.rfpId),
          onMutateResult.previousRfp,
        );
      }
      onError?.(error, variables, onMutateResult, context);
    },
    onSuccess,
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });
      queryClient.invalidateQueries({ queryKey: ["coverage"] });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfp(variables.rfpId),
      });
    },
  });
};

/**
 * Hook to duplicate a benefit plan within its group.
 *
 * Invalidates benefit plan group queries on success so the UI reflects the new plan.
 */
export const useDuplicateBenefitPlan = (
  options?: UseMutationOptions<
    BenefitPlanGroup,
    Error,
    { rfpId: string; benefitPlanId: string }
  >,
): UseMutationResult<
  BenefitPlanGroup,
  Error,
  { rfpId: string; benefitPlanId: string }
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();
  const { onSuccess, onError, ...restOptions } = options || {};
  const handleError = useApiErrorHandler({ onError });

  if (!axiosInstance) {
    throw new Error("Duplicate Benefit Plan API is not mocked");
  }

  return useMutation({
    ...restOptions,
    mutationFn: ({ benefitPlanId }) =>
      duplicateBenefitPlan(benefitPlanId, axiosInstance),
    onError: handleError,
    onSuccess: (data, variables, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });
      queryClient.invalidateQueries({
        queryKey: ["coverage"],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfp(variables.rfpId),
      });
      onSuccess?.(data, variables, ...rest);
    },
  });
};

/**
 * Hook to update a benefit plan's option name
 *
 * Invalidates benefit plan group queries on success so headers refresh.
 */
export const useUpdateBenefitPlanOptionName = (
  options?: UseMutationOptions<
    void,
    Error,
    { rfpId: string; benefitPlanId: string; optionName: PlanOptionNameEnum }
  >,
): UseMutationResult<
  void,
  Error,
  { rfpId: string; benefitPlanId: string; optionName: PlanOptionNameEnum }
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();
  const { onSuccess, onError, ...restOptions } = options || {};
  const handleError = useApiErrorHandler({ onError });

  if (!axiosInstance) {
    throw new Error("Update Benefit Plan Option Name API is not mocked");
  }

  return useMutation({
    ...restOptions,
    mutationFn: ({ benefitPlanId, optionName }) =>
      updateBenefitPlanOptionName(benefitPlanId, optionName, axiosInstance),
    onError: handleError,
    onSuccess: (data, variables, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: ["benefitPlanGroups", variables.rfpId],
      });
      queryClient.invalidateQueries({
        queryKey: ["coverage"],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rfp(variables.rfpId),
      });
      onSuccess?.(data, variables, ...rest);
    },
  });
};
